import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import type { AuthSessionResponse, WorkspaceMembership } from "@yagni/shared";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Spinner } from "../ui/spinner.js";
import { trackClientEvent } from "../analytics/index.js";
import { AUTHENTICATED_DEFAULT_PATH } from "../routes/defaults.js";

const AVATAR_COLORS = [
  "var(--agent-ops)",
  "var(--agent-analytics)",
  "var(--agent-pm)",
  "var(--agent-dev)",
  "var(--agent-comms)",
  "var(--agent-gtm)",
  "var(--agent-qa)",
];

function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
}

export function WorkspacesPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/auth/session", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("Not authenticated");
        return res.json() as Promise<AuthSessionResponse>;
      })
      .then(setSession)
      .catch(() => {
        void navigate("/auth", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleSelectWorkspace = useCallback(
    async (membership: WorkspaceMembership) => {
      if (
        session &&
        membership.workspace.id !== session.workspace.id
      ) {
        trackClientEvent("Workspace Switched", {
          from_workspace_id: session.workspace.id,
          to_workspace_id: membership.workspace.id,
        });
        await fetch("/api/auth/switch-workspace", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ workspaceId: membership.workspace.id }),
        });
      }
      window.location.href = AUTHENTICATED_DEFAULT_PATH;
    },
    [session],
  );

  const handleNewWorkspace = useCallback(() => {
    void navigate("/onboarding?new=true");
  }, [navigate]);

  if (loading || !session) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--canvas-field)", color: "var(--text-tertiary)" }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--canvas-field)" }}
    >
      <div className="max-w-2xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1
            className="text-2xl font-bold"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--text-primary)",
            }}
          >
            Your workspaces
          </h1>
          <Button type="button" onClick={handleNewWorkspace}>
            <Plus size={16} strokeWidth={2} />
            New workspace
          </Button>
        </div>

        {/* Workspace cards */}
        <div className="flex flex-col gap-3">
          {session.memberships.map((membership) => {
            const isActive =
              membership.workspace.id === session.workspace.id;
            return (
              <button
                key={membership.workspace.id}
                type="button"
                onClick={() => void handleSelectWorkspace(membership)}
                className="w-full flex items-center gap-4 px-5 py-4 rounded-[var(--radius-row)] border border-[var(--print-border)] bg-[var(--print-bg)] shadow-[var(--shadow-print)] text-left transition-colors hover:bg-[var(--print-bg-solid)]"
              >
                {/* Avatar */}
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-base shrink-0"
                  style={{
                    background: avatarColor(membership.workspace.id),
                  }}
                >
                  {membership.workspace.name.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <div
                    className="text-sm font-semibold truncate"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {membership.workspace.name}
                  </div>
                  <div
                    className="text-xs truncate mt-0.5"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {membership.workspace.slug}
                    {" \u00b7 "}
                    {membership.role.charAt(0).toUpperCase() +
                      membership.role.slice(1)}
                  </div>
                </div>

                {/* Active badge */}
                {isActive && (
                  <Badge
                    className="shrink-0 bg-[var(--accent)] text-[var(--text-inverse)]"
                  >
                    Active
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
